import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
} from "react";
import {
  api,
  type CzasotekstElementRole,
  type CzasotekstElements,
  type CzasotekstHookFrame,
  type CzasotekstHookFrameStyle,
  type CzasotekstOverlay,
  type CzasotekstOverlayBox,
  type CzasotekstOverlayText,
  type CzasotekstPack,
  type MediaAsset,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Loader2, Star } from "lucide-react";
import EditableColorSwatches from "@/components/EditableColorSwatches";

const CANVAS_W = 1080;
const CANVAS_H = 1920;
const PREVIEW_W = 270;
const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"] as const;
type Handle = (typeof HANDLES)[number];

/**
 * Przybliżone UI Instagrama na rolce 9:16 (1080×1920).
 * Szare pasy = tu IG zasłania treść (nick/audio, caption + dolny chrome, kolumna reakcji).
 */
const IG_REEL_UNSAFE = {
  top: 250,
  bottom: 450,
  right: 130,
} as const;

const ROLE_LABEL: Record<CzasotekstElementRole, string> = {
  hook: "Hook",
  cta: "Przeczytaj opis",
  arrow: "Strzałka",
};

const DEFAULT_BOX: Record<CzasotekstElementRole, CzasotekstOverlayBox> = {
  hook: { x: 80, y: 720, w: 920, h: 280, rotate: 0 },
  // Ponad dolną strefą IG (1920 − 450 = 1470).
  cta: { x: 200, y: 1280, w: 680, h: 96, rotate: 0 },
  arrow: { x: 460, y: 1380, w: 140, h: 90, rotate: 0 },
};

const DEFAULT_TEXT: Record<CzasotekstElementRole, CzasotekstOverlayText> = {
  hook: { font: "SourceSans3-Bold", size: 64, color: "#f8f5ef", align: "center" },
  cta: { font: "SourceSans3-Bold", size: 36, color: "#ffffff", align: "center" },
  arrow: { font: "SourceSans3-Bold", size: 36, color: "#ffffff", align: "center" },
};

const DEFAULT_PACK_ID = "pack_pill_brand";
/** Jak HOOKFRAME_TEXT_PADDING w czasotekst_compose.py */
const HOOKFRAME_TEXT_PADDING = 40;
/** Padding tekstu przy pack chrome / fallback (hook_pill_dark w katalogu). */
const DEFAULT_CHROME_TEXT_PADDING = 28;
const LINE_HEIGHT = 1.28;

function cssFontFromId(fontId?: string): { family: string; weight: number } {
  const id = String(fontId || "SourceSans3-Bold");
  const bold = /Bold/i.test(id);
  if (/SourceSans3/i.test(id)) return { family: "Source Sans 3", weight: bold ? 700 : 400 };
  if (/SourceSerif/i.test(id)) return { family: "Source Serif 4", weight: bold ? 700 : 400 };
  if (/Lora/i.test(id)) return { family: "Lora", weight: bold ? 700 : 400 };
  if (/Playfair/i.test(id)) return { family: "Playfair Display", weight: bold ? 700 : 400 };
  return { family: "Source Sans 3", weight: 700 };
}

let measureCtx: CanvasRenderingContext2D | null = null;

function measureTextWidth(text: string, fontCss: string): number {
  if (typeof document === "undefined") return text.length * 10;
  if (!measureCtx) {
    const canvas = document.createElement("canvas");
    measureCtx = canvas.getContext("2d");
  }
  if (!measureCtx) return text.length * 10;
  measureCtx.font = fontCss;
  return measureCtx.measureText(text).width;
}

/** Identyczny algorytm co `_wrap_lines` w czasotekst_compose.py */
function wrapLinesPillow(
  text: string,
  maxW: number,
  fontCss: string,
  maxLines = 4,
): string[] {
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let cur = "";
  for (const word of words) {
    const trial = cur ? `${cur} ${word}` : word;
    if (measureTextWidth(trial, fontCss) <= maxW || !cur) {
      cur = trial;
    } else {
      lines.push(cur);
      cur = word;
      if (lines.length >= maxLines) break;
    }
  }
  if (cur && lines.length < maxLines) lines.push(cur);
  return lines.slice(0, maxLines);
}

function clampBox(box: CzasotekstOverlayBox, role: CzasotekstElementRole): CzasotekstOverlayBox {
  const minW = role === "arrow" ? 80 : 120;
  const minH = role === "arrow" ? 60 : 64;
  let x = Number(box.x || 0);
  let y = Number(box.y || 0);
  let w = Math.max(minW, Number(box.w || minW));
  let h = Math.max(minH, Number(box.h || minH));
  if (x < 0) {
    w += x;
    x = 0;
  }
  if (y < 0) {
    h += y;
    y = 0;
  }
  if (x + w > CANVAS_W) w = CANVAS_W - x;
  if (y + h > CANVAS_H) h = CANVAS_H - y;
  return {
    x: Math.round(x),
    y: Math.round(y),
    w: Math.max(minW, Math.round(w)),
    h: Math.max(minH, Math.round(h)),
    rotate: Number(box.rotate || 0),
  };
}

/** Ściśnij box do proporcji PNG (contain → ramka edycyjna przycięta do zawartości). */
function shrinkBoxToAspect(
  box: CzasotekstOverlayBox,
  natW: number,
  natH: number,
  role: CzasotekstElementRole,
): CzasotekstOverlayBox {
  if (natW <= 0 || natH <= 0 || box.w <= 0 || box.h <= 0) return clampBox(box, role);
  const aspect = natW / natH;
  const boxAspect = box.w / box.h;
  if (Math.abs(aspect - boxAspect) < 0.03) return clampBox(box, role);
  let w = box.w;
  let h = box.h;
  if (boxAspect > aspect) {
    w = Math.round(box.h * aspect);
  } else {
    h = Math.round(box.w / aspect);
  }
  const x = Math.round(box.x + (box.w - w) / 2);
  const y = Math.round(box.y + (box.h - h) / 2);
  return clampBox({ ...box, x, y, w, h }, role);
}

function resizeFromHandle(
  start: { handle?: Handle; ox: number; oy: number; ow: number; oh: number },
  dx: number,
  dy: number,
  role: CzasotekstElementRole,
): CzasotekstOverlayBox {
  let x = start.ox;
  let y = start.oy;
  let w = start.ow;
  let h = start.oh;
  const handle = start.handle || "se";
  if (handle.includes("e")) w = start.ow + dx;
  if (handle.includes("s")) h = start.oh + dy;
  if (handle.includes("w")) {
    x = start.ox + dx;
    w = start.ow - dx;
  }
  if (handle.includes("n")) {
    y = start.oy + dy;
    h = start.oh - dy;
  }
  return clampBox({ x, y, w, h, rotate: 0 }, role);
}

function elementsFromPack(pack: CzasotekstPack, prev?: CzasotekstElements | null): Required<CzasotekstElements> {
  const ctaContent = String(prev?.cta?.content || "przeczytaj opis").trim() || "przeczytaj opis";
  const ink = pack.ink || "#f8f5ef";
  const ctaBg = pack.cta_bg || pack.accent || "#22c55e";
  const hex = (ctaBg.replace("#", "") || "22c55e").slice(0, 6);
  const bright = Number.parseInt(hex.slice(0, 2), 16) > 0xb0;
  const ctaInk = hex.toLowerCase().endsWith("00") ? ink : bright ? "#14532d" : "#ffffff";
  return {
    hook: {
      element_id: `pack:${pack.id}:hook`,
      frame_id: `pack:${pack.id}:hook`,
      box: clampBox({ ...DEFAULT_BOX.hook, ...(pack.boxes?.hook || {}) }, "hook"),
      text: { ...DEFAULT_TEXT.hook, color: ink },
      content: "",
      visible: true,
    },
    cta: {
      element_id: `pack:${pack.id}:cta`,
      frame_id: `pack:${pack.id}:cta`,
      box: clampBox({ ...DEFAULT_BOX.cta, ...(pack.boxes?.cta || {}) }, "cta"),
      text: { ...DEFAULT_TEXT.cta, color: ctaInk },
      content: ctaContent,
      visible: true,
    },
    arrow: {
      element_id: `pack:${pack.id}:arrow`,
      frame_id: `pack:${pack.id}:arrow`,
      box: clampBox({ ...DEFAULT_BOX.arrow, ...(pack.boxes?.arrow || {}) }, "arrow"),
      text: { ...DEFAULT_TEXT.arrow, color: ink },
      content: "",
      visible: true,
    },
  };
}

function normalizeRole(
  role: CzasotekstElementRole,
  raw?: CzasotekstOverlay | null,
): Required<CzasotekstOverlay> {
  const box = clampBox({ ...DEFAULT_BOX[role], ...(raw?.box || {}) }, role);
  const text = { ...DEFAULT_TEXT[role], ...(raw?.text || {}) };
  const elementId = String(raw?.element_id || raw?.frame_id || `pack:${DEFAULT_PACK_ID}:${role}`);
  const content =
    role === "cta"
      ? String(raw?.content || "przeczytaj opis").trim() || "przeczytaj opis"
      : String(raw?.content || "").trim();
  return {
    element_id: elementId,
    frame_id: elementId,
    box,
    text,
    content,
    visible: raw?.visible !== false,
  };
}

function normalizeElements(
  raw?: CzasotekstElements | null,
  overlay?: CzasotekstOverlay | null,
): Required<CzasotekstElements> {
  return {
    hook: normalizeRole("hook", raw?.hook || overlay || null),
    cta: normalizeRole("cta", raw?.cta || null),
    arrow: normalizeRole("arrow", raw?.arrow || null),
  };
}

function handleStyle(handle: Handle): CSSProperties {
  const common: CSSProperties = {
    position: "absolute",
    width: 10,
    height: 10,
    background: "#fff",
    border: "1px solid #b5614e",
    borderRadius: 2,
    zIndex: 3,
  };
  const map: Record<Handle, CSSProperties> = {
    nw: { ...common, left: -5, top: -5, cursor: "nwse-resize" },
    n: { ...common, left: "50%", top: -5, marginLeft: -5, cursor: "ns-resize" },
    ne: { ...common, right: -5, top: -5, cursor: "nesw-resize" },
    e: { ...common, right: -5, top: "50%", marginTop: -5, cursor: "ew-resize" },
    se: { ...common, right: -5, bottom: -5, cursor: "nwse-resize" },
    s: { ...common, left: "50%", bottom: -5, marginLeft: -5, cursor: "ns-resize" },
    sw: { ...common, left: -5, bottom: -5, cursor: "nesw-resize" },
    w: { ...common, left: -5, top: "50%", marginTop: -5, cursor: "ew-resize" },
  };
  return map[handle];
}

function bgCss(raw?: string): string {
  const v = String(raw || "#281f1c");
  if (v === "transparent" || (v.length === 9 && v.toLowerCase().endsWith("00"))) return "transparent";
  return v.slice(0, 7);
}

export default function CzasotekstComposeCanvas({
  packs = [],
  packId: packIdProp,
  elements: elementsProp,
  overlay: overlayProp,
  hook,
  videoUrl,
  videoAsset,
  scalePan,
  onChange,
  onPackChange,
  onSave,
  onCompose,
  onRegeneratePack,
  regenerating,
  saving,
  composing,
  ffmpegOk,
  hookFrameStyles = [],
  hookFrames = [],
  onGenerateHookFrame,
  generatingHookFrame,
  onStarHookFrame,
  onSelectHookFrame,
}: {
  packs?: CzasotekstPack[];
  packId?: string;
  elements?: CzasotekstElements | null;
  overlay?: CzasotekstOverlay | null;
  hook: string;
  videoUrl?: string;
  videoAsset?: MediaAsset | null;
  scalePan: { scale: number; x: number; y: number };
  onChange: (next: CzasotekstElements) => void;
  onPackChange: (packId: string, next: CzasotekstElements) => void;
  onSave: (elements: CzasotekstElements) => void;
  onCompose: (elements: CzasotekstElements) => void;
  onRegeneratePack?: (packId: string) => void;
  regenerating?: boolean;
  saving?: boolean;
  composing?: boolean;
  ffmpegOk?: boolean;
  hookFrameStyles?: CzasotekstHookFrameStyle[];
  hookFrames?: CzasotekstHookFrame[];
  onGenerateHookFrame?: (body: {
    style_id?: string;
    custom_prompt?: string;
    shadow?: boolean;
    cta_layout?: "under" | "sides" | "auto";
  }) => void;
  generatingHookFrame?: boolean;
  onStarHookFrame?: (frameId: string, starred: boolean) => void;
  onSelectHookFrame?: (frame: CzasotekstHookFrame) => void;
}) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [elements, setElements] = useState(() => normalizeElements(elementsProp, overlayProp));
  const [packId, setPackId] = useState(packIdProp || DEFAULT_PACK_ID);
  const [hookStyleId, setHookStyleId] = useState(hookFrameStyles[0]?.id || "");
  const [hookCustomPrompt, setHookCustomPrompt] = useState("");
  const [hookShadow, setHookShadow] = useState(false);
  const [ctaLayout, setCtaLayout] = useState<"under" | "sides" | "auto">("auto");
  const [hookLibTab, setHookLibTab] = useState<"all" | "starred" | "used">("all");
  const [lookMode, setLookMode] = useState<"pack" | "hookframe">("pack");
  const [activeRole, setActiveRole] = useState<CzasotekstElementRole>("hook");
  const [fontsReady, setFontsReady] = useState(0);
  const fittedChrome = useRef<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (!cancelled) setFontsReady((n) => n + 1);
    };
    if (typeof document !== "undefined" && document.fonts?.ready) {
      void document.fonts.ready.then(run);
      void document.fonts.load('700 64px "Source Sans 3"').then(run).catch(() => undefined);
    } else {
      run();
    }
    return () => {
      cancelled = true;
    };
  }, []);
  const drag = useRef<{
    kind: "move" | "resize";
    role: CzasotekstElementRole;
    handle?: Handle;
    sx: number;
    sy: number;
    ox: number;
    oy: number;
    ow: number;
    oh: number;
  } | null>(null);

  useEffect(() => {
    setElements(normalizeElements(elementsProp, overlayProp));
  }, [elementsProp, overlayProp]);

  useEffect(() => {
    if (packIdProp) setPackId(packIdProp);
  }, [packIdProp]);

  useEffect(() => {
    if (!hookStyleId && hookFrameStyles[0]?.id) setHookStyleId(hookFrameStyles[0].id);
  }, [hookFrameStyles, hookStyleId]);

  const previewScale = PREVIEW_W / CANVAS_W;
  const activePack = useMemo(
    () => packs.find((row) => row.id === packId) || packs[0],
    [packs, packId],
  );
  const active = elements[activeRole]!;
  const filteredHookFrames = useMemo(() => {
    if (hookLibTab === "starred") return hookFrames.filter((f) => f.starred);
    if (hookLibTab === "used") return hookFrames.filter((f) => (f.used_count || 0) > 0);
    return hookFrames;
  }, [hookFrames, hookLibTab]);
  const selectedHookFrameId = useMemo(() => {
    const eid = String(elements.hook?.element_id || elements.hook?.frame_id || "");
    return eid.startsWith("hookframe:") ? eid.slice("hookframe:".length) : "";
  }, [elements.hook?.element_id, elements.hook?.frame_id]);
  const selectedHookFrame = useMemo(
    () => hookFrames.find((f) => f.id === selectedHookFrameId),
    [hookFrames, selectedHookFrameId],
  );

  useEffect(() => {
    if (selectedHookFrameId) setLookMode("hookframe");
  }, [selectedHookFrameId]);

  const busy =
    Boolean(generatingHookFrame) ||
    Boolean(regenerating) ||
    Boolean(saving) ||
    Boolean(composing);
  const statusLabel = generatingHookFrame
    ? "Generuję komplet (hak + CTA)… (AI, ok. 20–60 s)"
    : regenerating
      ? "Przegenerowuję komplet wizualny…"
      : saving
        ? "Zapisuję skład…"
        : composing
          ? "Składam MP4…"
          : "Gotowe do pracy";
  const previewCaption =
    lookMode === "hookframe" && selectedHookFrameId
      ? `Komplet: hak + CTA (${selectedHookFrame?.cta_layout || "combo"}) · ${selectedHookFrame?.style_id || selectedHookFrameId.slice(0, 8)}`
      : `Zestaw: ${activePack?.label || packId}`;

  function emit(next: Required<CzasotekstElements>) {
    setElements(next);
    onChange(next);
  }

  function selectPack(pack: CzasotekstPack) {
    const next = elementsFromPack(pack, elements);
    setPackId(pack.id);
    setLookMode("pack");
    setElements(next);
    onPackChange(pack.id, next);
  }

  function patchRole(role: CzasotekstElementRole, patch: Partial<CzasotekstOverlay>) {
    setElements((prev) => {
      const merged = normalizeRole(role, { ...prev[role], ...patch });
      const next = { ...prev, [role]: merged } as Required<CzasotekstElements>;
      onChange(next);
      return next;
    });
  }

  function toCanvas(clientX: number, clientY: number) {
    const el = stageRef.current;
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    return {
      x: ((clientX - r.left) / r.width) * CANVAS_W,
      y: ((clientY - r.top) / r.height) * CANVAS_H,
    };
  }

  function onBoxDown(e: PointerEvent<HTMLDivElement>, role: CzasotekstElementRole) {
    e.preventDefault();
    e.stopPropagation();
    setActiveRole(role);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const p = toCanvas(e.clientX, e.clientY);
    const box = elements[role]!.box!;
    drag.current = {
      kind: "move",
      role,
      sx: p.x,
      sy: p.y,
      ox: box.x,
      oy: box.y,
      ow: box.w,
      oh: box.h,
    };
  }

  function onResizeDown(e: PointerEvent<HTMLButtonElement>, handle: Handle, role: CzasotekstElementRole) {
    e.preventDefault();
    e.stopPropagation();
    setActiveRole(role);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const p = toCanvas(e.clientX, e.clientY);
    const box = elements[role]!.box!;
    drag.current = {
      kind: "resize",
      role,
      handle,
      sx: p.x,
      sy: p.y,
      ox: box.x,
      oy: box.y,
      ow: box.w,
      oh: box.h,
    };
  }

  function onPointerMove(e: PointerEvent<HTMLDivElement>) {
    if (!drag.current) return;
    const role = drag.current.role;
    const cur = elements[role]!;
    const p = toCanvas(e.clientX, e.clientY);
    const dx = p.x - drag.current.sx;
    const dy = p.y - drag.current.sy;
    if (drag.current.kind === "resize") {
      patchRole(role, { box: resizeFromHandle(drag.current, dx, dy, role) });
      return;
    }
    patchRole(role, {
      box: clampBox(
        {
          ...cur.box!,
          x: drag.current.ox + dx,
          y: drag.current.oy + dy,
        },
        role,
      ),
    });
  }

  function onPointerUp() {
    drag.current = null;
  }

  function layerLabel(role: CzasotekstElementRole) {
    if (role === "hook") return hook.trim() || "Hak na ekranie";
    if (role === "cta") return elements.cta?.content || "przeczytaj opis";
    return "";
  }

  function packChromeUrl(role: CzasotekstElementRole) {
    if (role === "hook") {
      const eid = String(elements.hook?.element_id || elements.hook?.frame_id || "");
      if (eid.startsWith("hookframe:") && !eid.endsWith(":cta")) {
        const fid = eid.slice("hookframe:".length);
        if (fid) return `${api.czasotekstHookFrameAssetUrl(fid, "hook")}?v=${fid}`;
      }
    }
    if (role === "cta") {
      const eid = String(elements.cta?.element_id || elements.cta?.frame_id || "");
      if (eid.startsWith("hookframe:") && eid.endsWith(":cta")) {
        const fid = eid.slice("hookframe:".length, -":cta".length);
        if (fid) return `${api.czasotekstHookFrameAssetUrl(fid, "cta")}?v=${fid}`;
      }
    }
    if (!packId || !activePack?.ready) return "";
    return `${api.czasotekstPackAssetUrl(packId, role)}?v=${packId}`;
  }

  function fitChromeBox(role: CzasotekstElementRole, chromeUrl: string, natW: number, natH: number) {
    const eid = String(elements[role]?.element_id || "");
    if (!eid.startsWith("hookframe:")) return;
    const key = `${role}:${eid}:${chromeUrl}`;
    if (fittedChrome.current[key] === `${natW}x${natH}`) return;
    const cur = elements[role]?.box;
    if (!cur) return;
    const nextBox = shrinkBoxToAspect(cur, natW, natH, role);
    if (
      nextBox.w === cur.w &&
      nextBox.h === cur.h &&
      nextBox.x === cur.x &&
      nextBox.y === cur.y
    ) {
      fittedChrome.current[key] = `${natW}x${natH}`;
      return;
    }
    fittedChrome.current[key] = `${natW}x${natH}`;
    patchRole(role, { box: nextBox });
  }

  function renderLayer(role: CzasotekstElementRole) {
    const layout = elements[role]!;
    if (layout.visible === false) return null;
    const box = layout.box!;
    const ink = layout.text?.color || activePack?.ink || "#f8f5ef";
    const fallbackBg =
      role === "cta"
        ? bgCss(activePack?.cta_bg || activePack?.accent)
        : role === "arrow"
          ? bgCss(activePack?.accent || activePack?.cta_bg)
          : bgCss(activePack?.bg_hint || activePack?.accent);
    const chrome = packChromeUrl(role);
    const size = Number(layout.text?.size || DEFAULT_TEXT[role].size);
    const align = layout.text?.align || "center";
    const selected = activeRole === role;
    const eid = String(layout.element_id || layout.frame_id || "");
    const isHookframe = eid.startsWith("hookframe:");
    let padding = isHookframe ? HOOKFRAME_TEXT_PADDING : chrome ? DEFAULT_CHROME_TEXT_PADDING : 16;
    if (isHookframe) {
      padding = Math.max(16, Math.min(padding, Math.floor(box.h / 7), Math.floor(box.w / 16)));
    }
    const { family, weight } = cssFontFromId(layout.text?.font);
    const label = layerLabel(role);
    void fontsReady;
    let drawSize = size;
    let lines: string[] = [];
    if (role !== "arrow" && label) {
      const innerW = Math.max(40, box.w - 2 * padding);
      const innerH = Math.max(40, box.h - 2 * padding);
      for (let guard = 0; guard < 40; guard += 1) {
        const fontCss = `${weight} ${drawSize}px "${family}"`;
        lines = wrapLinesPillow(label, innerW, fontCss);
        const blockH = drawSize * LINE_HEIGHT * Math.max(1, lines.length);
        if (blockH <= innerH || drawSize <= 22) break;
        drawSize -= 2;
      }
    }
    const fontSize = drawSize * previewScale;
    const lineH = drawSize * LINE_HEIGHT * previewScale;
    const padPx = padding * previewScale;
    return (
      <div
        key={role}
        className={cn("absolute cursor-move", selected ? "z-20" : "z-10")}
        style={{
          left: box.x * previewScale,
          top: box.y * previewScale,
          width: box.w * previewScale,
          height: box.h * previewScale,
          transform: box.rotate ? `rotate(${box.rotate}deg)` : undefined,
          background: chrome ? "transparent" : fallbackBg,
          borderRadius: 12 * previewScale,
          border: selected ? "1.5px solid #b5614e" : "1px solid rgba(255,255,255,0.2)",
          color: ink,
          fontFamily: family,
          fontWeight: weight,
          fontSize,
          lineHeight: LINE_HEIGHT,
          textAlign: align as "left" | "center" | "right",
          display: "flex",
          alignItems: "center",
          justifyContent: align === "left" ? "flex-start" : align === "right" ? "flex-end" : "center",
          overflow: "hidden",
        }}
        onPointerDown={(e) => onBoxDown(e, role)}
      >
        {chrome ? (
          <img
            src={chrome}
            alt=""
            className={cn(
              "pointer-events-none absolute inset-0 h-full w-full",
              isHookframe ? "object-fill" : "object-contain",
            )}
            draggable={false}
            onLoad={(e) => {
              const img = e.currentTarget;
              if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                fitChromeBox(role, chrome, img.naturalWidth, img.naturalHeight);
              }
            }}
          />
        ) : null}
        {role === "arrow" ? null : (
          <div
            className="relative z-[1] box-border w-full"
            style={{
              paddingLeft: padPx,
              paddingRight: padPx,
              textAlign: align as "left" | "center" | "right",
            }}
          >
            {lines.map((line, i) => (
              <div
                key={`${role}-${i}-${line}`}
                style={{
                  height: lineH,
                  lineHeight: `${lineH}px`,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                {line}
              </div>
            ))}
          </div>
        )}
        {selected
          ? HANDLES.map((handle) => (
              <button
                key={handle}
                type="button"
                aria-label={`resize-${role}-${handle}`}
                style={handleStyle(handle)}
                onPointerDown={(e) => onResizeDown(e, handle, role)}
              />
            ))
          : null}
      </div>
    );
  }

  return (
    <div className="grid gap-3 lg:grid-cols-[auto_minmax(0,1fr)]">
      <div className="space-y-2">
        <div
          ref={stageRef}
          className="relative overflow-hidden rounded border bg-black shadow-sm select-none"
          style={{ width: PREVIEW_W, height: PREVIEW_W * (CANVAS_H / CANVAS_W) }}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {videoUrl ? (
            <video
              key={videoAsset?.id || videoUrl}
              className="absolute inset-0 h-full w-full object-cover"
              src={videoUrl}
              muted
              playsInline
              loop
              autoPlay
              style={{
                transform: `scale(${scalePan.scale}) translate(${scalePan.x / scalePan.scale}px, ${scalePan.y / scalePan.scale}px)`,
              }}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-zinc-900 text-[11px] text-zinc-400">
              Wybierz film
            </div>
          )}
          {(["hook", "cta", "arrow"] as CzasotekstElementRole[]).map(renderLayer)}
          {/* Strefy UI IG: nad warstwami, bez blokady drag (pointer-events-none). */}
          <div
            className="pointer-events-none absolute inset-x-0 top-0 z-30 bg-zinc-400/50"
            style={{
              height: IG_REEL_UNSAFE.top * previewScale,
              backgroundImage:
                "repeating-linear-gradient(-45deg, transparent, transparent 6px, rgba(0,0,0,0.18) 6px, rgba(0,0,0,0.18) 12px)",
            }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 z-30 bg-zinc-400/50"
            style={{
              height: IG_REEL_UNSAFE.bottom * previewScale,
              backgroundImage:
                "repeating-linear-gradient(-45deg, transparent, transparent 6px, rgba(0,0,0,0.18) 6px, rgba(0,0,0,0.18) 12px)",
            }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-y-0 right-0 z-30 bg-zinc-400/45"
            style={{
              width: IG_REEL_UNSAFE.right * previewScale,
              backgroundImage:
                "repeating-linear-gradient(-45deg, transparent, transparent 6px, rgba(0,0,0,0.18) 6px, rgba(0,0,0,0.18) 12px)",
            }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-x-0 z-30 border-t-2 border-dashed border-amber-200/90"
            style={{ top: IG_REEL_UNSAFE.top * previewScale }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-x-0 z-30 border-b-2 border-dashed border-amber-200/90"
            style={{
              bottom: IG_REEL_UNSAFE.bottom * previewScale,
            }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-y-0 z-30 border-l-2 border-dashed border-amber-200/80"
            style={{ right: IG_REEL_UNSAFE.right * previewScale }}
            aria-hidden
          />
        </div>
        <p className="text-[11px] text-muted-foreground">{previewCaption}</p>
        <p className="max-w-[270px] text-[10px] leading-snug text-muted-foreground">
          Szare pasy = UI Instagrama (góra: nick/audio, dół: opis + pasek, prawo: reakcje). CTA i strzałkę
          trzymaj w jasnym środku, inaczej IG ucina jak „przeczytaj opis”.
        </p>
      </div>

      <div className="flex min-w-0 flex-col gap-3">
        <div
          className={cn(
            "sticky top-0 z-10 flex items-center gap-2 rounded border px-2.5 py-1.5 text-[11px]",
            busy ? "border-brand/40 bg-brand/5 text-slate-800" : "border-border bg-muted/30 text-muted-foreground",
          )}
          role="status"
          aria-live="polite"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-brand" /> : null}
          <span className={busy ? "font-medium" : undefined}>{statusLabel}</span>
        </div>

        <section className="space-y-2 rounded border border-border/80 p-2.5">
          <p className="text-[11px] font-semibold text-slate-800">Krok 1 · Wygląd</p>
          <div className="grid grid-cols-2 gap-1.5">
            <button
              type="button"
              className={cn(
                "rounded border px-2 py-1.5 text-left text-[11px]",
                lookMode === "pack" ? "border-brand bg-brand/5 font-medium" : "border-border hover:bg-muted/40",
              )}
              onClick={() => setLookMode("pack")}
              disabled={busy}
            >
              Zestaw gotowy
              <span className="mt-0.5 block text-[10px] font-normal text-muted-foreground">
                hook + CTA + strzałka
              </span>
            </button>
            <button
              type="button"
              className={cn(
                "rounded border px-2 py-1.5 text-left text-[11px]",
                lookMode === "hookframe" ? "border-brand bg-brand/5 font-medium" : "border-border hover:bg-muted/40",
              )}
              onClick={() => setLookMode("hookframe")}
              disabled={busy}
            >
              Własna ramka haka
              <span className="mt-0.5 block text-[10px] font-normal text-muted-foreground">
                AI: hak + CTA ze strzałką
              </span>
            </button>
          </div>

          {lookMode === "pack" ? (
            <div className="grid max-h-56 grid-cols-2 gap-1.5 overflow-auto sm:grid-cols-3 xl:grid-cols-4">
              {packs.map((pack) => (
                <button
                  key={pack.id}
                  type="button"
                  disabled={busy}
                  className={cn(
                    "rounded border px-1.5 py-1.5 text-left text-[10px] leading-tight",
                    packId === pack.id && !selectedHookFrameId
                      ? "border-brand bg-brand/5"
                      : "border-border hover:bg-muted/40",
                    busy && "opacity-60",
                  )}
                  onClick={() => selectPack(pack)}
                >
                  <span className="mb-1 flex h-7 w-full items-center gap-0.5 overflow-hidden rounded">
                    <span className="h-full flex-1" style={{ background: bgCss(pack.bg_hint || pack.accent) }} />
                    <span className="h-full w-1/3" style={{ background: bgCss(pack.cta_bg || pack.accent) }} />
                    <span
                      className="flex h-full w-6 items-center justify-center text-[11px] font-bold"
                      style={{ background: bgCss(pack.accent), color: pack.ink || "#fff" }}
                    >
                      ↓
                    </span>
                  </span>
                  <span className="font-medium">{pack.label}</span>
                  {pack.ready ? null : <span className="ml-1 text-amber-700">· gen</span>}
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {selectedHookFrameId ? (
                <p className="text-[10px] text-muted-foreground">
                  Hak + CTA ze strzałką z Twojego kompletu. Osobna warstwa Strzałka jest ukryta.
                </p>
              ) : (
                <p className="text-[10px] text-muted-foreground">
                  Generuj komplet: ramka haka oraz połączony blok „przeczytaj opis” ze strzałką.
                </p>
              )}
              <label className="block text-[11px]">
                Styl
                <select
                  className="mt-0.5 w-full rounded border px-2 py-1 text-xs"
                  value={hookStyleId}
                  disabled={busy}
                  onChange={(e) => setHookStyleId(e.target.value)}
                >
                  {hookFrameStyles.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-[11px]">
                Własny prompt (opcjonalnie)
                <textarea
                  className="mt-0.5 w-full rounded border px-2 py-1 text-xs min-h-[56px]"
                  placeholder="Dopisz brief estetyczny… (nadpisuje styl)"
                  value={hookCustomPrompt}
                  disabled={busy}
                  onChange={(e) => setHookCustomPrompt(e.target.value)}
                />
              </label>
              <div>
                <p className="mb-1 text-[11px]">Układ CTA + strzałka</p>
                <div className="flex flex-wrap gap-1">
                  {(
                    [
                      ["auto", "Auto"],
                      ["under", "Pod napisem"],
                      ["sides", "Po bokach"],
                    ] as const
                  ).map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      disabled={busy}
                      className={cn(
                        "rounded border px-2 py-0.5 text-[10px]",
                        ctaLayout === id ? "border-brand bg-brand/5 font-medium" : "border-border",
                      )}
                      onClick={() => setCtaLayout(id)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-[11px]">
                <input
                  type="checkbox"
                  checked={hookShadow}
                  disabled={busy}
                  onChange={(e) => setHookShadow(e.target.checked)}
                />
                Cień (dodawany w postprocess, nie w AI)
              </label>
              <Button
                size="sm"
                variant="outline"
                disabled={busy || !onGenerateHookFrame}
                onClick={() =>
                  onGenerateHookFrame?.({
                    style_id: hookCustomPrompt.trim() ? "" : hookStyleId,
                    custom_prompt: hookCustomPrompt.trim() || undefined,
                    shadow: hookShadow,
                    cta_layout: ctaLayout,
                  })
                }
              >
                {generatingHookFrame ? "Generuję…" : "Generuj komplet"}
              </Button>

              <p className="pt-1 text-[11px] font-medium text-slate-700">Twoje ramki</p>
              <div className="flex flex-wrap gap-1">
                {(
                  [
                    ["all", "Wszystkie"],
                    ["starred", "Gwiazdka"],
                    ["used", "Użyte"],
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    disabled={busy}
                    className={cn(
                      "rounded border px-2 py-0.5 text-[10px]",
                      hookLibTab === id ? "border-brand bg-brand/5 font-medium" : "border-border",
                    )}
                    onClick={() => setHookLibTab(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="grid max-h-40 grid-cols-2 gap-1.5 overflow-auto sm:grid-cols-3">
                {filteredHookFrames.length === 0 ? (
                  <p className="col-span-full text-[10px] text-muted-foreground">Brak ramek w tej zakładce.</p>
                ) : (
                  filteredHookFrames.map((frame) => (
                    <div
                      key={frame.id}
                      className={cn(
                        "relative rounded border p-0.5",
                        selectedHookFrameId === frame.id ? "border-brand ring-1 ring-brand/40" : "border-border",
                      )}
                    >
                      <button
                        type="button"
                        className="flex w-full gap-0.5 overflow-hidden rounded bg-zinc-900"
                        disabled={busy}
                        onClick={() => onSelectHookFrame?.(frame)}
                        title={frame.style_id || frame.id}
                      >
                        <img
                          src={`${api.czasotekstHookFrameAssetUrl(frame.id, "hook")}?v=${frame.id}`}
                          alt=""
                          className="h-12 w-1/2 object-contain"
                        />
                        <img
                          src={`${api.czasotekstHookFrameAssetUrl(frame.id, "cta")}?v=${frame.id}`}
                          alt=""
                          className="h-12 w-1/2 object-contain"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display = "none";
                          }}
                        />
                      </button>
                      <button
                        type="button"
                        className={cn(
                          "absolute right-0.5 top-0.5 rounded bg-white/90 p-0.5",
                          frame.starred ? "text-brand" : "text-muted-foreground",
                        )}
                        title={frame.starred ? "Zdejmij gwiazdkę" : "Gwiazdka"}
                        disabled={busy}
                        onClick={() => onStarHookFrame?.(frame.id, !frame.starred)}
                      >
                        <Star className={cn("h-3 w-3", frame.starred ? "fill-current" : "")} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </section>

        <section className="space-y-2 rounded border border-border/80 p-2.5">
          <p className="text-[11px] font-semibold text-slate-800">Krok 2 · Układ na filmie</p>
          <p className="text-[10px] text-muted-foreground">Przeciągnij ramki na podglądzie po lewej.</p>
          <div className="flex flex-wrap gap-1">
            {(["hook", "cta", "arrow"] as CzasotekstElementRole[]).map((role) => (
              <button
                key={role}
                type="button"
                className={cn(
                  "rounded border px-2 py-1 text-[11px]",
                  activeRole === role ? "border-brand bg-brand/5 font-medium" : "border-border hover:bg-muted/40",
                )}
                onClick={() => setActiveRole(role)}
              >
                {ROLE_LABEL[role]}
              </button>
            ))}
          </div>

          {activeRole === "cta" ? (
            <label className="block text-[11px]">
              Tekst CTA
              <input
                className="mt-0.5 w-full rounded border px-2 py-1 text-xs"
                value={elements.cta?.content || "przeczytaj opis"}
                onChange={(e) => patchRole("cta", { content: e.target.value })}
              />
            </label>
          ) : null}

          {activeRole !== "arrow" ? (
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="text-[11px]">
                Rozmiar tekstu {active.text?.size || DEFAULT_TEXT[activeRole].size}
                <input
                  type="range"
                  min={activeRole === "cta" ? 22 : 32}
                  max={activeRole === "cta" ? 56 : 96}
                  step={2}
                  value={active.text?.size || DEFAULT_TEXT[activeRole].size}
                  className="w-full"
                  onChange={(e) =>
                    patchRole(activeRole, {
                      text: { ...active.text, size: Number(e.target.value) },
                    })
                  }
                />
              </label>
              <label className="text-[11px]">
                Kolor
                <div className="mt-0.5">
                  <EditableColorSwatches
                    hideLabel
                    label="Kolor tekstu"
                    value={active.text?.color || "#f8f5ef"}
                    onChange={(hex) =>
                      patchRole(activeRole, {
                        text: { ...active.text, color: hex },
                      })
                    }
                  />
                </div>
              </label>
            </div>
          ) : null}
        </section>

        <section className="sticky bottom-0 z-10 space-y-2 rounded border border-border/80 bg-background/95 p-2.5 shadow-sm backdrop-blur-sm">
          <p className="text-[11px] font-semibold text-slate-800">Krok 3 · Gotowe</p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => onSave(elements)}
            >
              {saving ? "Zapisuję…" : "Zapisz skład"}
            </Button>
            <Button
              size="sm"
              variant="brand"
              className="min-w-[9rem]"
              disabled={busy || ffmpegOk === false}
              onClick={() => onCompose(elements)}
            >
              {composing ? "Składam…" : "Złóż MP4"}
            </Button>
            {lookMode === "pack" && onRegeneratePack && packId ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => onRegeneratePack(packId)}
              >
                {regenerating ? "Generuję…" : "Przegeneruj komplet"}
              </Button>
            ) : null}
          </div>
          {lookMode === "pack" && activePack?.blurb ? (
            <p className="text-[11px] text-muted-foreground">{activePack.blurb}</p>
          ) : null}
        </section>
      </div>
    </div>
  );
}

export function czasotekstVideoUrl(assetId: string) {
  return api.mediaFile(assetId, false);
}
